import { useDialogShadow } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_ID_ATTRIBUTE_NAME, GEOMETRY_ATTRIBUTE } from "../../../../evergis/constants";
import { isSystemAttribute } from "../../../../evergis/core/attributes/attributes";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { useAttributes } from "../../../../hooks/attributes/useAttributes";
import { useBulkOperationsToggle } from "../../../../hooks/useBulkOperationsToggle";
import { Attributes } from "../../DialogComponents/Attributes/Attributes";
import { useAttributesToRender } from "../../DialogComponents/Attributes/hooks/useAttributesToRender";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent } from "../../DialogComponents/styled";
import { useAttributesErrors } from "../../EditResource/hooks/useAttributesErrors";
import { AttributeWithType } from "../../NewLayer/components/AttributeWithType";
import { useContext } from "../hooks/useContext";
import { useImportContext } from "../importContext";
import { ImportAttribute, ImportType } from "../types";
import { getLayerDefinition } from "../utils/getLayerDefinition";
import { ImportAttributeItem } from "./ImportAttributeItem";
import { ImportWizard } from "./ImportWizard";

// eslint-disable-next-line max-lines-per-function
export const ImportAttributes: FC = () => {
  const { t } = useTranslation("common");
  const context = useContext();
  const { shadows, setScrollContainer } = useDialogShadow();
  const {
    prevStep,
    nextStep,
    dataSchema,
    attributes: importAttributes,
    setAttributes: setContextAttributes,
    importType,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    updateSysAttribute,
    geometryType,
  } = useImportContext();

  const [attributes, { updateAttribute, setAttributes }] = useAttributes(importAttributes);
  const attributeErrors = useAttributesErrors(attributes, sysAttributes);
  const attributesToRender = useAttributesToRender(attributes) as ImportAttribute[];
  const excludePrivateAttributes = useCallback(
    (attribute: ImportAttribute) => {
      const isVisibleAttribute = !!attributesToRender.find(({ id }) => id === attribute.id);

      return !isVisibleAttribute || isSystemAttribute(attribute);
    },
    [attributesToRender],
  );
  const idAttribute = useMemo(() => {
    const idAttributeFromSchema =
      importType !== ImportType.KML
        ? (dataSchema as SchemaParsed).idAttribute
        : getLayerDefinition(dataSchema[0].children).idAttribute;

    return idAttributeFromSchema || DEFAULT_ID_ATTRIBUTE_NAME;
  }, [importType, dataSchema]);
  const geometryAttribute = useMemo(() => {
    const geometryAttributeFromSchema =
      importType !== ImportType.KML
        ? (dataSchema as SchemaParsed).geometryAttribute
        : getLayerDefinition(dataSchema[0].children).geometryAttribute;

    return geometryAttributeFromSchema || GEOMETRY_ATTRIBUTE;
  }, [importType, dataSchema]);
  const visibleAttributes = useMemo(
    () =>
      attributesToRender.filter(
        ({ disabled, expression, name }) =>
          !disabled && !expression && ![idAttribute, geometryAttribute].includes(name),
      ),
    [attributesToRender, idAttribute, geometryAttribute],
  );

  const [allAttributes, toggleAllAttributes] = useBulkOperationsToggle<ImportAttribute>({
    items: attributes,
    setItems: setAttributes,
    column: "disabled",
    reverse: true,
    isChecked: ({ name, disabled }) => {
      const isIdField = name === idAttribute;
      const isChecked = !disabled;

      return isIdField || isChecked;
    },
    excludeItem: excludePrivateAttributes,
  });

  const onApply = useCallback(() => {
    setContextAttributes(attributes);
    nextStep();
  }, [attributes, nextStep, setContextAttributes]);

  const { firstRow } = dataSchema as SchemaParsed;

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} extra={<ImportWizard />} />
      <DialogContent ref={setScrollContainer}>
        <Attributes
          attributes={attributes}
          setAttributes={setAttributes}
          allAttributes={allAttributes}
          showTitle={false}
          isEdit={false}
          toggleAllAttributes={toggleAllAttributes}
          sysAttributes={sysAttributes}
          addSysAttributes={addSysAttributes}
        >
          {(attribute: ImportAttribute) =>
            attribute.sysAttribute ? (
              <AttributeWithType
                attributeName={attribute.attributeName}
                id={attribute.id}
                type={attribute.type}
                subType={attribute.subType}
                expression={attribute.expression}
                sysAttribute
                onDeleteSysAttribute={deleteSysAttribute}
                onChangeSysAttribute={updateSysAttribute}
                geometryType={geometryType}
              />
            ) : (
              <ImportAttributeItem
                idField={attribute.idAttribute}
                idAttribute={idAttribute}
                geometryAttribute={geometryAttribute}
                attribute={attribute}
                updateAttribute={updateAttribute}
                error={attributeErrors.get(attribute.id)}
                firstRowValue={firstRow ? firstRow[attribute.originalName] : void 0}
              />
            )
          }
        </Attributes>
      </DialogContent>
      <DialogActions
        applyText={t("actions.next")}
        applyDisabled={!!attributeErrors.size || visibleAttributes.length === 0}
        backButtonText={t("import.back", { ns: "dialogs" })}
        onBackButton={prevStep}
        onApply={onApply}
      />
    </FullScreenDialog>
  );
};
